// @flow
import { getClient } from 'api'
import Spinner from 'components/Spinner'
import sortBy from 'lodash/sortBy'
import * as React from 'react'
import type { Response } from 'types/response'
import Flex from 'ui/atoms/Flex'
import Scrollable from 'ui/atoms/Scrollable'
import Table from 'ui/atoms/Table'

type Patient = {
  id?: string,
  first_name?: string,
  last_name?: string,
  date_birth?: string,
}

type Props = {
  patient: Patient,
}

function renderLoadingState() {
  return (
    <Table.Row>
      <Table.Column colspan={2}>
        <Flex justify="center">
          <Spinner color={'onLight'} size={64} />
        </Flex>
      </Table.Column>
    </Table.Row>
  )
}

function renderEmptyState() {
  return (
    <Table.Row>
      <Table.Column colspan={2}>No forms have been completed.</Table.Column>
    </Table.Row>
  )
}

function renderErrorState() {
  return (
    <Table.Row>
      <Table.Column colspan={2}>An error occurred.</Table.Column>
    </Table.Row>
  )
}

function TabletModePatientFormFormsTable({ patient }: Props): React.Node {
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState(false)
  const [responses, setResponses] = React.useState<Response[]>([])

  React.useEffect(() => {
    if (!patient.id) return

    async function load() {
      setLoading(true)
      try {
        const queryParams = new URLSearchParams({
          includePacketResponses: 'true',
          submittedOnly: 'true',
        })
        const client = await getClient(patient.id, queryParams)
        setResponses(sortBy(client.responses, 'submitted_at').reverse())
      } catch (err) {
        setError(err)
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [patient])

  const renderForms = React.useCallback(() => {
    return responses.map((response) => {
      if (!response.form) return null

      const submittedAt = new Date(response?.submitted_at ?? '')

      return (
        <Table.Row key={response.id}>
          <Table.Column $truncate style={{ maxWidth: 240 }}>
            {response.form?.name}
          </Table.Column>
          <Table.Column>{submittedAt.toLocaleDateString()}</Table.Column>
        </Table.Row>
      )
    })
  }, [responses])

  const renderContent = React.useCallback(() => {
    if (loading) return renderLoadingState()

    if (error) return renderErrorState()

    if (responses.length === 0) return renderEmptyState()

    return renderForms()
  }, [error, loading, renderForms, responses.length])

  return (
    <Scrollable>
      <Table>
        <Table.Head columns={['Form Title', 'Date Completed']} />
        <Table.Body>{renderContent()}</Table.Body>
      </Table>
    </Scrollable>
  )
}

export default TabletModePatientFormFormsTable
